import { useCallback, useState, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Auth from "../services/auth";
import TextBox from "../components/controls/form/TextBox";
import { useTranslation } from "react-i18next";
import PubSub from "../services/PubSub";
import LandingPageWrapper from "../components/LandingPageWrapper";
import ErrorBoundary from "../common/routeWrappers/ErrorBoundary";

const Login = () => {
    const defaultEmail = window.localStorage.getItem("email") || "";
    const [translate] = useTranslation();
    const [rememberMe, setRememberMe] = useState(window.localStorage.getItem("rememberMe") === "true");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [buttonDisabled, setButtonDisabled] = useState(false);

    const login = useCallback(async ({ email, password }) => {
        setButtonDisabled(true);
        if (rememberMe) {
            window.localStorage.setItem("email", email);
        } else {
            window.localStorage.removeItem("email");
        }
        window.localStorage.setItem("rememberMe", rememberMe);
        const response = await Auth.login({ email, password });

        setButtonDisabled(false);
        if (response && !response.error) {
            const redirect = searchParams.get("redirect");
            // Always go to / first — Home will either apply the redirect directly (single-domain)
            // or pass it to ChangeDomainModal (cross-domain), which navigates there after domain selection.
            navigate(redirect ? `/?redirect=${encodeURIComponent(redirect)}` : "/");
        } else {
            PubSub.publish("show_info", response.data.error);
        }
    }, [navigate, rememberMe, searchParams]);

    useEffect(() => {
        window.sessionStorage.clear();
    }, []);

    return (<ErrorBoundary>
        <LandingPageWrapper>
            <Formik
                initialValues={{
                    email: defaultEmail,
                    password: ""
                }}
                validationSchema={Yup.object({
                    email: Yup.string()
                        .email(translate("OTM_INVALID_EMAIL"))
                        .required(" "),
                    password: Yup.string()
                        .min(4, translate("OTM_CHARACTERS_OR_MORE", { minCharacters: 4 }))
                        .required(" ")
                })}
                onSubmit={login}
            >
                <Form>
                    <TextBox
                        id="login-email"
                        placeholder={translate("OTM_EMAIL")}
                        type="email"
                        name="email"
                        css=""
                    />
                    <TextBox placeholder={translate("OTM_PASSWORD")} type="password" name="password" css="mt-3" />
                    <div className="mt-3">
                        <Link to={`/reset-password?email=${document.getElementById('login-email')?.value || ''}`}>
                            {translate("OTM_PASSWORD_FORGOTTEN")}
                        </Link>
                    </div>
                    <div className="mt-3 form-check">
                        <input type="checkbox" className="form-check-input" id="rememberMe"
                            checked={rememberMe} onChange={() => setRememberMe(!rememberMe)}></input>
                        <label htmlFor="rememberMe" className="form-check-label">{translate("OTM_REMEMBER_ME")}</label>
                    </div>
                    <div className="text-center mt-3">
                        <Button type="submit" className="btn-primary" disabled={buttonDisabled}>{translate("OTM_LOGIN")}</Button>
                    </div>
                </Form>
            </Formik>
        </LandingPageWrapper>
    </ErrorBoundary>
    );
};

export default Login;
